import { put, select } from 'redux-saga/effects';
import orderStatusActions from '@/redux/orderStatus/actions';
import { type RootState } from '@/types/app';
import { type BasketNotFoundSource } from '@/types/orderStatus';
import * as ErrorReporter from '@/utils/errorReporter';
import { isBasketNotFoundError } from '@/utils/errors';

export interface BasketNotFoundCallContext {
  /** The basket the failing call was made for. */
  basketId: string | null | undefined;
  /** The store location in the failing call's path. */
  locationId: number | string | null | undefined;
  source: BasketNotFoundSource;
  /** TEAM-14291: whether a Toast payment was authorized before the failure. */
  toastPaymentWasAuthorized?: boolean;
}

/**
 * TEAM-15690: lost baskets the diner has walked away from by pressing "Start a new cart". A call
 * for one still in flight at the press can 404 after it; that response belongs to a cart that no
 * longer exists and must neither report again nor put the notice back over the new cart.
 * Module-scoped because DESTROY_ORDER (part of the press) clears `lostBasketId`; a reload drops it
 * along with every call that could still answer for the old basket.
 */
const abandonedLostBaskets = new Set<string>();

/** Called by the "Start a new cart" press, before it destroys the order. */
export const abandonLostBasket = (basketId: string | null | undefined): void => {
  if (basketId) {
    abandonedLostBaskets.add(basketId);
  }
};

/**
 * TEAM-15690: a basket call coo-api answered 404 for the diner's current checkout basket. Moves the
 * order to BASKET_NOT_FOUND — the inline "Start a new cart" notice — and reports it once. Returns
 * whether it did; on `false` the caller handles the error exactly as before.
 *
 * Returns false, so the caller's own handling runs, for:
 * - anything `isBasketNotFoundError` rejects: another status, or an unresolved id in the path;
 * - a basket that is no longer the checkout basket (a restart or store switch while the call was in
 *   flight): its 404 says nothing about the basket the diner is on now.
 *
 * Returns true without dispatching or reporting for a basket the diner abandoned with "Start a new
 * cart" (`abandonLostBasket`): the 404 is already handled, and the caller must stop.
 *
 * A later 404 for the same basket is not reported again (see `IOrderStatusState.lostBasketId`). It
 * is still dispatched: the reducer ignores it while the notice is up, and if something has moved
 * the order off BASKET_NOT_FOUND since (a forced `orderPending()`, a re-initialization reusing the
 * same id), it puts the notice back instead of leaving the caller's state stranded.
 */
export function* surfaceBasketNotFound(
  error: unknown,
  ctx: BasketNotFoundCallContext,
): Generator<unknown, boolean> {
  const { basketId, locationId } = ctx;
  if (!basketId || !locationId || !isBasketNotFoundError(error, basketId, locationId)) {
    return false;
  }
  if (abandonedLostBaskets.has(basketId)) {
    return true;
  }

  const state = (yield select()) as RootState;
  const currentBasketId = state.app.basket.checkoutBasket?.id;
  if (currentBasketId && currentBasketId !== basketId) {
    return false;
  }

  if (state.app.orderStatus.lostBasketId !== basketId) {
    ErrorReporter.reportBasketNotFound(error, {
      basketId,
      locationId,
      organizationId: state.app.organization?.organization?.id,
      source: ctx.source,
    });
  }
  yield put(orderStatusActions.basketNotFound(basketId, ctx.source, ctx.toastPaymentWasAuthorized));
  return true;
}
